import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export async function getMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function exchangeSession(session_id) {
  const { data } = await api.post("/auth/google", { session_id });
  return data;
}

export async function logout() {
  await api.post("/auth/logout");
}

export async function getBilling() {
  const { data } = await api.get("/billing/me");
  return data;
}

export async function createCheckout(plan, origin_url) {
  const { data } = await api.post("/payments/create-checkout", { plan, origin_url });
  return data;
}

export async function pollPaymentStatus(session_id) {
  const { data } = await api.get(`/payments/status/${session_id}`);
  return data;
}

export async function claimPricingTableSession(session_id) {
  const { data } = await api.post("/payments/claim-pricing-table-session", { session_id });
  return data;
}

export async function cancelSubscription() {
  const { data } = await api.post("/billing/cancel-subscription");
  return data;
}

export async function resumeSubscription() {
  const { data } = await api.post("/billing/resume-subscription");
  return data;
}

export async function fetchDemo() {
  const { data } = await api.get("/analytics/demo");
  return data;
}

export async function uploadCsv(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/analytics/parse-csv", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function createAnalysis(payload) {
  const { data } = await api.post("/analyses/create", payload);
  return data;
}

export async function listAnalyses() {
  const { data } = await api.get("/analyses");
  return data;
}

export async function getAnalysis(id) {
  const { data } = await api.get(`/analyses/${id}`);
  return data;
}

export async function createShare(analysisId) {
  const { data } = await api.post(`/analyses/${analysisId}/share`);
  return data;
}

export async function revokeShare(analysisId) {
  const { data } = await api.delete(`/analyses/${analysisId}/share`);
  return data;
}

export async function getShared(token) {
  // public — no auth needed
  const { data } = await api.get(`/share/${token}`);
  return data;
}

export async function getAdminStats() {
  const { data } = await api.get("/admin/stats");
  return data;
}

export async function getAdminSubscribers({ limit = 200, skip = 0 } = {}) {
  const { data } = await api.get("/admin/subscribers", { params: { limit, skip } });
  return data;
}

export const adminSubscribersCsvUrl = `${API}/admin/subscribers.csv`;

export async function lookupUser(email) {
  const { data } = await api.get(`/admin/lookup-user`, { params: { email } });
  return data;
}

export async function grantPlan({ email, plan, days, reason }) {
  const { data } = await api.post(`/admin/grant-plan`, { email, plan, days, reason });
  return data;
}

export async function trackShareReferralClick(token, network) {
  // public — fire-and-forget; ignore errors. network is optional.
  try {
    const url = network
      ? `/share/${token}/referral-click?network=${encodeURIComponent(network)}`
      : `/share/${token}/referral-click`;
    await api.post(url);
  } catch {
    // intentionally swallow — this is best-effort analytics
  }
}

export async function getShareStats(token) {
  const { data } = await api.get(`/share/${token}/stats`);
  return data;
}

export async function getBranding() {
  const { data } = await api.get("/me/branding");
  return data;
}

export async function updateBranding(payload) {
  const { data } = await api.put("/me/branding", payload);
  return data;
}

export async function gaStart() {
  const { data } = await api.get("/ga/oauth/start");
  return data;
}

export async function gaStatus() {
  const { data } = await api.get("/ga/status");
  return data;
}

export async function gaDisconnect() {
  const { data } = await api.delete("/ga/disconnect");
  return data;
}

export async function gaProperties() {
  const { data } = await api.get("/ga/properties");
  return data;
}

export async function gaData(payload) {
  const { data } = await api.post("/ga/data", payload);
  return data;
}

export async function setGaDefaultProperty(payload) {
  const { data } = await api.put("/ga/default-property", payload);
  return data;
}

export async function getEmailPrefs() {
  const { data } = await api.get("/me/email-prefs");
  return data;
}

export async function setEmailPrefs(enabled) {
  const { data } = await api.put("/me/email-prefs", { weekly_email_enabled: enabled });
  return data;
}

export async function sendTestWeeklyEmail() {
  const { data } = await api.post("/me/weekly-email/send-test");
  return data;
}

// ---- Backlink Tracker (owner-only) ----
export async function listBacklinks() {
  const { data } = await api.get("/admin/backlinks");
  return data;
}

export async function createBacklink(payload) {
  const { data } = await api.post("/admin/backlinks", payload);
  return data;
}

export async function updateBacklink(id, payload) {
  const { data } = await api.patch(`/admin/backlinks/${id}`, payload);
  return data;
}

export async function deleteBacklink(id) {
  const { data } = await api.delete(`/admin/backlinks/${id}`);
  return data;
}

